// Chunk 125: Enterprise SSO — API client
import apiClient from './client';

// ─── Types ────────────────────────────────────────────────────────────────────

export type SsoProviderType = 'google' | 'entra' | 'okta' | 'saml';

export interface SsoConfig {
  configured: boolean;
  providerType?: SsoProviderType;
  entityId?: string | null;
  ssoUrl?: string | null;
  certificate?: string | null;
  clientId?: string | null;
  clientSecret?: string | null;
  tenantId?: string | null;
  attributeMapping?: Record<string, string>;
  autoProvisionUsers?: boolean;
  defaultRole?: string;
  enabled?: boolean;
  createdAt?: string;
  updatedAt?: string;
  spMetadataUrl?: string | null;
  acsUrl?: string | null;
}

export interface SsoUpdatePayload {
  providerType: SsoProviderType;
  entityId?: string | null;
  ssoUrl?: string | null;
  certificate?: string | null;
  clientId?: string | null;
  clientSecret?: string | null;
  tenantId?: string | null;
  attributeMapping?: Record<string, string>;
  autoProvisionUsers?: boolean;
  defaultRole?: string;
  enabled?: boolean;
}

export interface SsoLookupResult {
  orgId: string;
  orgName: string;
  providerType: SsoProviderType;
}

// ─── API functions ────────────────────────────────────────────────────────────

/** Resolve an organisation domain to orgId + provider (public endpoint) */
export async function lookupSsoDomain(domain: string): Promise<SsoLookupResult> {
  const res = await apiClient.get<SsoLookupResult>('/auth/sso/lookup', {
    params: { domain },
  });
  return res.data;
}

/** Get SSO configuration for an org (secrets redacted) — org_admin only */
export async function getOrgSsoConfig(orgId: string): Promise<SsoConfig> {
  const res = await apiClient.get<SsoConfig>(`/auth/organisations/${orgId}/sso`);
  return res.data;
}

/** Create or update SSO configuration — org_admin only */
export async function updateOrgSsoConfig(
  orgId: string,
  payload: SsoUpdatePayload,
): Promise<{ success: boolean; message: string; enabled: boolean }> {
  const res = await apiClient.put<{ success: boolean; message: string; enabled: boolean }>(
    `/auth/organisations/${orgId}/sso`,
    payload,
  );
  return res.data;
}

/**
 * Returns the URL to initiate an SSO flow for a given orgId.
 * The browser should navigate to this URL (window.location.href) so the
 * redirect chain to the IdP works correctly.
 */
export function getSsoInitiateUrl(orgId: string): string {
  const base = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
  return `${base}/auth/sso/${orgId}`;
}

/** Returns the SP metadata XML URL for SAML configuration */
export function getSpMetadataUrl(orgId: string): string {
  const base = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
  return `${base}/auth/sso/${orgId}/metadata`;
}
